import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Truck,
  X,
  MapPin,
  Loader2,
  Route,
  Gauge,
  CalendarClock,
  Radio,
  Package,
  CircleDot,
  Maximize2,
  Minimize2,
  FileText,
  Plus,
  Minus,
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MultiSelectWithSearchInput from "../../components/infiniteMultiSelect/withInfiniteMultiSelect";
import { tntLinkageGetTripDetails } from "./tntLinkageApi";
import { mapTripDetailsToTracking, formatTrackingDateTime } from "./tripTrackingUtils";
import "./tripTrackingModal.css";
import PropTypes from "prop-types";

const EMPTY_TRACKING = {
  shipment: {
    vehicle: "—",
    id: "—",
    tat: "—",
    sta: "—",
    eta: "—",
    totalKm: 0,
    remainingKm: 0,
    progress: 0,
    consignee: "—",
    consignor: "—",
    consigneeAddress: "",
    consignorAddress: "",
    ewbNumber: "—",
    ewbExpiry: "—",
    originPincode: "—",
    destinationPincode: "—",
    destination: "—",
  },
  log: [],
  waypoints: [],
  livePosition: { title: "—", sub: "" },
  nextStop: { title: "—", sub: "" },
  products: [],
};

const WAYPOINT_COLOR = {
  origin: "#12b886",
  active: "#3b5bdb",
  destination: "#f76707",
  intermediate: "#12b886",
};

// Longitude that splits the map so permanent labels open away from the route.
const LABEL_FLIP_LONGITUDE = 76;

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildWaypointTooltipHtml(wp) {
  const title = escapeHtml(wp.label);
  let timeHtml = "";
  // Only Destination and Checkpoint show date/time on the map tip.
  if (wp.trackedAt && wp.type !== "origin") {
    const { date, time } = formatTrackingDateTime(wp.trackedAt);
    const formattedDateTime = `${date} · ${time}`;
    const escapedDateTime = escapeHtml(formattedDateTime);
    timeHtml = `<div class="ttm-map-tip-time">${escapedDateTime}</div>`;
  }
  const loc = escapeHtml(wp.location || wp.sub || "");
  const locHtml = loc ? `<div class="ttm-map-tip-loc">${loc}</div>` : "";
  const typeLabels = {
    origin: "Origin",
    destination: "Destination",
  };
  const typeLabel = typeLabels[wp.type] ?? "Checkpoint";
  return `<div class="ttm-map-tip">
    <div class="ttm-map-tip-type">${typeLabel}</div>
    <div class="ttm-map-tip-title">${title}</div>
    ${timeHtml}
    ${locHtml}
  </div>`;
}

function hasValue(value) {
  const s = value != null ? String(value).trim() : "";
  return Boolean(s) && s !== "—" && s !== "~";
}

function displayValue(value, fallback = "—") {
  return hasValue(value) ? String(value).trim() : fallback;
}

function joinRoute(from, to, separator) {
  const start = hasValue(from) ? from : null;
  const end = hasValue(to) ? to : null;
  if (start && end) return `${start}${separator}${end}`;
  return start || end || null;
}

function resolveWaypointColor({ isOrigin, isActive, isDest }) {
  if (isOrigin) return WAYPOINT_COLOR.origin;
  if (isActive) return WAYPOINT_COLOR.active;
  if (isDest) return WAYPOINT_COLOR.destination;
  return WAYPOINT_COLOR.intermediate;
}

function resolveWaypointSize({ isActive, isDest, isIntermediate }) {
  if (isActive) return 14;
  if (isDest) return 12;
  return isIntermediate ? 5 : 10;
}

function resolveWaypointStyle(wp, originId, destId) {
  const isOrigin = wp.id === originId;
  const isActive = wp.status === "active";
  const isDest = wp.id === destId && wp.status === "pending";
  const isIntermediate = !isOrigin && !isActive && !isDest;

  return {
    isActive,
    showPermanentTip: isOrigin || isActive || isDest,
    color: resolveWaypointColor({ isOrigin, isActive, isDest }),
    size: resolveWaypointSize({ isActive, isDest, isIntermediate }),
  };
}

function buildTooltipOptions(wp, size, showPermanentTip) {
  if (!showPermanentTip) {
    return {
      permanent: false,
      direction: "top",
      offset: [0, -(size / 2 + 6)],
      className: "map-hover-tooltip",
      sticky: true,
      opacity: 0.98,
    };
  }

  const opensRight = wp.lng < LABEL_FLIP_LONGITUDE;
  return {
    permanent: true,
    direction: opensRight ? "right" : "left",
    offset: [opensRight ? size / 2 + 4 : -(size / 2 + 4), 0],
    className: "map-hover-tooltip map-label-tooltip",
    sticky: false,
    opacity: 0.98,
  };
}

function collectRouteSegments(waypoints) {
  const covered = waypoints
    .filter((w) => w.status !== "pending")
    .map((w) => [w.lat, w.lng]);

  const activeWp = waypoints.find((w) => w.status === "active");
  const destWp = waypoints.find((w) => w.status === "pending");
  const remaining =
    activeWp && destWp
      ? [
        [activeWp.lat, activeWp.lng],
        [destWp.lat, destWp.lng],
      ]
      : [];

  return { covered, remaining };
}

function createRouteMap(container) {
  const map = L.map(container, { zoomControl: true, attributionControl: false });

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap',
  }).addTo(map);

  return map;
}

function drawRoutePolylines(map, covered, remaining) {
  if (covered.length > 1) {
    L.polyline(covered, { color: "#3b5bdb", weight: 7, opacity: 0.1 }).addTo(map);
    L.polyline(covered, { color: "#3b5bdb", weight: 3.5, opacity: 0.95 }).addTo(map);
  }

  if (remaining.length > 1) {
    L.polyline(remaining, {
      color: "#94a3b8",
      weight: 2.5,
      opacity: 0.65,
      dashArray: "10 8",
    }).addTo(map);
  }
}

function addLiveVehicleMarkers(map, wp, color) {
  const rippleIcon = L.divIcon({
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    html: `<div style="width:36px;height:36px;border-radius:50%;background:${color}22;animation:mapRipple 1.8s ease-out infinite;"></div>`,
  });
  L.marker([wp.lat, wp.lng], { icon: rippleIcon, interactive: false, zIndexOffset: -10 }).addTo(map);

  const truckDivIcon = L.divIcon({
    className: "",
    iconSize: [26, 26],
    iconAnchor: [13, 28],
    html: `<div style="width:26px;height:26px;border-radius:8px;background:#1f36c7;border:2.5px solid white;box-shadow:0 4px 12px rgba(31,54,199,0.35);display:flex;align-items:center;justify-content:center;">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
    </div>`,
  });
  L.marker([wp.lat + 0.1, wp.lng], { icon: truckDivIcon, zIndexOffset: 20 }).addTo(map);
}

function renderWaypointMarker(map, wp, style) {
  const { color, size, isActive, showPermanentTip } = style;

  if (isActive) addLiveVehicleMarkers(map, wp, color);

  const dotIcon = L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 2px 6px ${color}40;"></div>`,
  });

  const marker = L.marker([wp.lat, wp.lng], { icon: dotIcon, zIndexOffset: 10 }).addTo(map);
  marker.bindTooltip(buildWaypointTooltipHtml(wp), buildTooltipOptions(wp, size, showPermanentTip));
}

function KpiCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`ttm-kpi${accent ? " ttm-kpi--accent" : ""}`}>
      <div className="ttm-kpi-icon">
        <Icon size={13} aria-hidden />
      </div>
      <div className="ttm-kpi-body">
        <span className="ttm-kpi-label">{label}</span>
        <span className="ttm-kpi-value">{value}</span>
        {sub ? <span className="ttm-kpi-sub">{sub}</span> : null}
      </div>
    </div>
  );
}

KpiCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]).isRequired,
  sub: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.node,
  ]),
  accent: PropTypes.bool,
};

function TimelineItem({ entry, isLast, isActive }) {
  return (
    <div className={`ttm-tl-item${isActive ? " ttm-tl-item--live" : ""}`}>
      <div className="ttm-tl-rail" aria-hidden>
        <span className={`ttm-tl-dot${isActive ? " ttm-tl-dot--live" : ""}`}>
          {isActive ? <Radio size={7} /> : <CircleDot size={7} />}
        </span>
        {!isLast ? <span className="ttm-tl-line" /> : null}
      </div>
      <div className="ttm-tl-content">
        <div className="ttm-tl-meta">
          <time className="ttm-tl-time">
            {displayValue(entry.date.replace(/\s\d{4}$/, ""), "—")}
            {hasValue(entry.time) ? ` · ${entry.time}` : ""}
          </time>
          {isActive ? <span className="ttm-badge ttm-badge--live">Live</span> : null}
        </div>
        <p className="ttm-tl-title">{displayValue(entry.location)}</p>
        {hasValue(entry.notes) && entry.notes !== entry.location ? (
          <p className="ttm-tl-notes" title={entry.notes}>{entry.notes}</p>
        ) : null}
      </div>
    </div>
  );
}

TimelineItem.propTypes = {
  entry: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    location: PropTypes.string,
    notes: PropTypes.string,
  }).isRequired,
  isLast: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
};

function TrackingMapPanel({ waypoints, isOpen, expanded, onToggleExpand }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return undefined;
    const timer = window.setTimeout(() => mapRef.current?.invalidateSize(), 150);
    return () => window.clearTimeout(timer);
  }, [isOpen, expanded]);

  useEffect(() => {
    if (!containerRef.current || !isOpen) return undefined;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    if (!waypoints.length) return undefined;

    const { covered, remaining } = collectRouteSegments(waypoints);

    const map = createRouteMap(containerRef.current);
    mapRef.current = map;

    drawRoutePolylines(map, covered, remaining);

    const originId = waypoints[0]?.id;
    const destId = waypoints[waypoints.length - 1]?.id;
    waypoints.forEach((wp) => {
      renderWaypointMarker(map, wp, resolveWaypointStyle(wp, originId, destId));
    });

    const allPoints = [...covered, ...remaining];
    if (allPoints.length > 0) {
      map.fitBounds(L.latLngBounds(allPoints), { padding: [32, 32] });
      window.setTimeout(() => map.invalidateSize(), 100);
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [waypoints, isOpen]);

  return (
    <div className="ttm-map-hero">
      <div ref={containerRef} className="ttm-map" />

      {!waypoints.length ? (
        <div className="ttm-map-empty">
          <MapPin size={18} aria-hidden />
          <span>Route data unavailable</span>
        </div>
      ) : null}

      <button
        type="button"
        className="ttm-map-expand"
        onClick={onToggleExpand}
        aria-label={expanded ? "Collapse tracking view" : "Expand tracking view"}
        title={expanded ? "Collapse view" : "Expand view"}
      >
        {expanded ? <Minimize2 size={14} aria-hidden /> : <Maximize2 size={14} aria-hidden />}
      </button>
    </div>
  );
}

TrackingMapPanel.propTypes = {
  waypoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      lat: PropTypes.number,
      lng: PropTypes.number,
    })
  ),
  isOpen: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
};

function ProductListSection({ products }) {
  const [expanded, setExpanded] = useState(false);

  if (!products.length) return null;

  return (
    <div className="ttm-product-list">
      <button
        type="button"
        className="ttm-product-list-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <Package size={10} aria-hidden />
        <span>Product List</span>
        {expanded ? (
          <Minus size={10} aria-hidden />
        ) : (
          <Plus size={10} aria-hidden />
        )}
      </button>
      {expanded ? (
        <ul className="ttm-product-list-items">
          {products.map((p) => (
            <li key={p.prodId ?? p.productName} className="ttm-product-list-item">
              <span className="ttm-product-list-name" title={p.productName}>
                {p.productName}
              </span>
              <span className="ttm-product-list-qty">
                {p.quantity} {p.uom || "unit"}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

ProductListSection.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      prodId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      productName: PropTypes.string,
      quantity: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      uom: PropTypes.string,
    })
  ),
};

function ShipmentKpiStrip({ shipment, checkpointCount, hasEvents, pincodeLabel }) {
  const hasDistance = shipment.totalKm > 0;

  return (
    <section className="ttm-kpi-strip" aria-label="Shipment KPIs">
      <KpiCard
        icon={Gauge}
        label="Journey"
        value={`${shipment.progress}%`}
        sub={hasDistance ? `${shipment.remainingKm.toLocaleString()} km left` : "Distance pending"}
        accent
      />
      <KpiCard
        icon={Route}
        label="Distance"
        value={
          hasDistance ? `${(shipment.totalKm - shipment.remainingKm).toLocaleString()} km` : "—"
        }
        sub={hasDistance ? `of ${shipment.totalKm.toLocaleString()} km` : null}
      />
      <KpiCard icon={CalendarClock} label="ETA" value={displayValue(shipment.eta)} />
      <KpiCard
        icon={MapPin}
        label="STA"
        value={displayValue(shipment.sta)}
        sub={pincodeLabel || "—"}
      />
      <KpiCard
        icon={Radio}
        label="Checkpoints"
        value={String(checkpointCount)}
        sub={hasEvents ? "toll crossings" : "No events"}
      />
      <KpiCard
        icon={FileText}
        label="E-Way Bill"
        value={displayValue(shipment.ewbNumber)}
        sub={hasValue(shipment.ewbExpiry) ? `Exp ${shipment.ewbExpiry}` : null}
      />
    </section>
  );
}

ShipmentKpiStrip.propTypes = {
  shipment: PropTypes.shape({
    totalKm: PropTypes.number,
    remainingKm: PropTypes.number,
    progress: PropTypes.number,
    eta: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
    sta: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
    ewbNumber: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    ewbExpiry: PropTypes.string,
  }).isRequired,

  checkpointCount: PropTypes.number.isRequired,
  hasEvents: PropTypes.bool.isRequired,
  pincodeLabel: PropTypes.string,
};


function ShipmentMetaSummary({ routeLine, addressLine, products, productListKey }) {
  const hasMeta = Boolean(routeLine || addressLine || products.length > 0);
  if (!hasMeta) return null;

  return (
    <div className="ttm-meta-compact">
      {routeLine ? (
        <p className="ttm-meta-line ttm-meta-line--primary" title={routeLine}>
          {routeLine}
        </p>
      ) : null}
      {addressLine ? (
        <p className="ttm-meta-line ttm-meta-line--secondary" title={addressLine}>
          {addressLine}
        </p>
      ) : null}
      <ProductListSection key={productListKey} products={products} />
    </div>
  );
}

ShipmentMetaSummary.propTypes = {
  routeLine: PropTypes.string,
  addressLine: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      name: PropTypes.string,
    })
  ),
  productListKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};


function MovementTimeline({ log }) {
  if (log.length === 0) {
    return (
      <div className="ttm-empty">
        <Radio size={16} aria-hidden />
        <p>No movement events yet</p>
      </div>
    );
  }

  return log.map((entry, i) => (
    <TimelineItem
      key={`${entry.date}-${entry.time}-${entry.location}-${i}`}
      entry={entry}
      isLast={i === log.length - 1}
      isActive={entry.status === "active"}
    />
  ));
}

MovementTimeline.propTypes = {
  log: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      status: PropTypes.string,
    })
  ).isRequired,
};

function useTripDetails(open, selectedTripId) {
  const [tripDetails, setTripDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!open || !selectedTripId) {
      setTripDetails(null);
      setFetchError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setTripDetails(null);

    tntLinkageGetTripDetails(selectedTripId)
      .then((data) => {
        if (!cancelled) setTripDetails(data);
      })
      .catch((err) => {
        if (!cancelled) setFetchError(err?.message || "Failed to load trip details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedTripId]);

  return { tripDetails, loading, fetchError };
}

function useModalEscape({ open, expanded, onCollapse, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (expanded) onCollapse();
      else onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onCollapse, expanded]);
}

function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

export default function TripTrackingModal({
  open,
  tripId,
  row,
  tripIds = [],
  rowsByTripId = {},
  alertType = "Supplier Insights",
  onClose,
}) {
  const [selectedTripId, setSelectedTripId] = useState(tripId || "");
  const [expanded, setExpanded] = useState(false);

  const availableTripIds = useMemo(() => {
    const fromList = Array.isArray(tripIds) ? tripIds.filter(Boolean) : [];
    if (fromList.length > 0) return fromList;
    return tripId ? [tripId] : [];
  }, [tripIds, tripId]);

  useEffect(() => {
    if (tripId) setSelectedTripId(tripId);
  }, [tripId, open]);

  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  const activeRow = rowsByTripId[selectedTripId] || row;
  const { tripDetails, loading, fetchError } = useTripDetails(open, selectedTripId);

  const tracking = useMemo(() => {
    if (!tripDetails) return EMPTY_TRACKING;
    return mapTripDetailsToTracking(tripDetails, activeRow);
  }, [tripDetails, activeRow]);

  const { shipment, log, waypoints, products } = tracking;

  const handleTripIdChange = useCallback((_label, value) => {
    const next = Array.isArray(value) ? value[0] : value;
    setSelectedTripId(next ? String(next) : "");
  }, []);

  const collapseExpanded = useCallback(() => setExpanded(false), []);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  const pincodeLabel = useMemo(
    () => joinRoute(shipment.originPincode, shipment.destinationPincode, " → "),
    [shipment]
  );

  const consignorConsigneeLine = useMemo(
    () => joinRoute(shipment.consignor, shipment.consignee, " → "),
    [shipment]
  );

  const addressRouteLine = useMemo(
    () => joinRoute(shipment.consignorAddress, shipment.destination, " to "),
    [shipment]
  );

  useModalEscape({ open, expanded, onCollapse: collapseExpanded, onClose });
  useBodyScrollLock(open);

  if (!open) return null;

  // Portalled to body: the routed page sits inside a `main` with its own
  // overflow/stacking context, which otherwise traps this overlay under the app header.
  return createPortal(
    <div className="ttm-backdrop">
      <dialog
        className={`ttm-shell${expanded ? ' ttm-shell--expanded' : ''}`}
        open
        aria-labelledby="ttm-title"
        onCancel={onClose}
      >
        <header className="ttm-topbar">
          <div className="ttm-topbar-left">
            <span className="ttm-brand-icon" aria-hidden>
              <Truck size={14} />
            </span>
            <h1 id="ttm-title" className="ttm-title">
              Tracking Details{' '}
              <span className="ttm-vehicle-number">
                {shipment.vehicleNumber ? `- ${shipment.vehicleNumber}` : ''}
              </span>
            </h1>
          </div>

          <div className="ttm-topbar-meta">
            <div className="ttm-topbar-item">
              <span className="ttm-topbar-item-label">Carrier</span>
              <span className="ttm-topbar-item-value" title={shipment.vehicle}>
                {displayValue(shipment.vehicle, '—')}
              </span>
            </div>
            <div className="ttm-topbar-item ttm-topbar-item--trip">
              <span className="ttm-topbar-item-label">Trip ID</span>
              <div className="ttm-trip-picker ttm-trip-picker--header">
                <MultiSelectWithSearchInput
                  label="Trip ID"
                  options={availableTripIds}
                  filterValues={selectedTripId ? [selectedTripId] : []}
                  handleFilter={handleTripIdChange}
                  disabled={loading}
                  type={alertType}
                  isMultiSelect={false}
                  isLabelShow={false}
                  showSelectAll={false}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            className="ttm-action"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={15} aria-hidden />
          </button>
        </header>

        {fetchError ? (
          <div className="ttm-alert" role="alert">
            {fetchError}
          </div>
        ) : null}

        {loading ? (
          <div className="ttm-loading" aria-busy="true">
            <Loader2 size={24} className="ttm-spinner" aria-hidden />
            <span>Syncing live shipment data…</span>
          </div>
        ) : (
          <>
            <ShipmentKpiStrip
              shipment={shipment}
              checkpointCount={Math.max(0, log.length - 1)}
              hasEvents={log.length > 0}
              pincodeLabel={pincodeLabel}
            />

            <div className="ttm-progress-rail" aria-hidden>
              <div
                className="ttm-progress-fill"
                style={{ width: `${shipment.progress}%` }}
              />
            </div>

            <div className="ttm-workspace">
              <TrackingMapPanel
                waypoints={waypoints}
                isOpen={open}
                expanded={expanded}
                onToggleExpand={toggleExpanded}
              />

              <aside className="ttm-rail">
                <div className="ttm-rail-head">
                  <h2 className="ttm-rail-title">Movement Timeline</h2>
                </div>

                <ShipmentMetaSummary
                  routeLine={consignorConsigneeLine}
                  addressLine={addressRouteLine}
                  products={products}
                  productListKey={selectedTripId}
                />

                <div className="ttm-timeline">
                  <MovementTimeline log={log} />
                </div>
              </aside>
            </div>
          </>
        )}
      </dialog>
    </div>,
    document.body
  );
}

TripTrackingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  tripId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  row: PropTypes.object,
  tripIds: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])
  ),
  rowsByTripId: PropTypes.object,
  alertType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
